import React from 'react';
import RiskAssessmentComponent from '../components/riskassessment/RiskAssessment';

/**
 * ProfileTab – static summary & recommendations block + Risk Assessment.
 *
 * Props
 *  ─ emergencyFund: { sixMonthGoal: number }
 *  ─ questions: array of questions for risk assessment (optional)
 *  ─ calculateResults: function to calculate risk assessment results (optional)
 *  ─ QuestionSlider: component for question slider (optional)
 *  ─ ProgressBar: component for progress bar (optional)
 *  ─ CryptoReportView: component for detailed report view (optional)
 *  ─ logo: logo image for risk assessment (optional)
 *  ─ riskAssessmentData: saved assessment data (optional)
 *  ─ setRiskAssessmentData: function to update assessment data (optional)
 */
const ProfileTab = ({ 
  emergencyFund,
  questions,
  calculateResults,
  QuestionSlider,
  ProgressBar,
  CryptoReportView,
  logo,
  riskAssessmentData,
  setRiskAssessmentData
}) => (
  <div className="space-y-6">
    {/* Original Financial Profile Summary */}
    <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold mb-6">Financial Profile Summary</h3>

      {/* Two-column summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Income sources */}
        <div>
          <h4 className="font-medium mb-3 text-arc-muted">Income Sources</h4>
          <div className="space-y-2">
            {[
              ['Primary Salary', 'Active'],
              ['Business Revenue', 'Variable'],
              ['Rental Income', 'Passive'],
              ['Crypto Income', 'Variable'],
              ['Affiliate Income', 'Variable'],
            ].map(([label, status]) => (
              <div key={label} className="flex justify-between">
                <span>{label}</span>
                <span className="font-semibold">{status}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Health metrics */}
        <div>
          <h4 className="font-medium mb-3 text-arc-muted">Financial Health Metrics</h4>
          <div className="space-y-2">
            {[
              ['Debt-to-Income Ratio', 'Low', 'text-green-600'],
              ['Emergency Fund Status', 'Needs Attention', 'text-red-600'],
              ['Investment Diversification', 'Moderate', 'text-yellow-600'],
              ['FIRE Progress', 'On Track', 'text-blue-600'],
            ].map(([label, value, color]) => (
              <div key={label} className="flex justify-between">
                <span>{label}</span>
                <span className={`font-semibold ${color}`}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="mt-6 p-4 bg-white/[0.03] rounded-lg">
        <h4 className="font-medium mb-2">Key Recommendations</h4>
        <ul className="text-sm space-y-1 text-arc-muted list-disc list-inside">
          <li>
            Build emergency fund to 6&nbsp;months of expenses ($
            {emergencyFund.sixMonthGoal.toLocaleString()})
          </li>
          <li>Continue aggressive debt payoff – focus on highest-interest debts</li>
          <li>Consider rebalancing crypto-heavy investment portfolio</li>
          <li>Track and categorise large expense items for better budgeting</li>
          <li>Maintain consistent income diversification strategy</li>
        </ul>
      </div>
    </div>

    {/* Risk Assessment Component */}
    {questions && calculateResults && QuestionSlider && ProgressBar && CryptoReportView && (
      <RiskAssessmentComponent
        questions={questions}
        calculateResults={calculateResults}
        QuestionSlider={QuestionSlider}
        ProgressBar={ProgressBar}
        CryptoReportView={CryptoReportView}
        logo={logo}
        riskAssessmentData={riskAssessmentData}
        setRiskAssessmentData={setRiskAssessmentData}
      />
    )}
  </div>
);

export default ProfileTab;